import { useCallback, useState } from 'react'
import { Cargando, Problema, Vacio } from '../componentes/Estados.js'
import { Icono } from '../componentes/Icono.js'
import { PastillaVitrina } from '../componentes/Pastilla.js'
import {
  MesasDestacadas,
  Punteros,
  TarjetaTorneo,
  rondaDelMomento,
} from '../componentes/Vitrina.js'
import { useCarga } from '../carga.js'
import { obtenerRepositorio } from '../datos/index.js'
import type { EstadoVitrina, PosicionPublica, RondaPublica, TorneoPublico } from '../datos/index.js'
import { cuantosCompiten, fechaLarga } from '../formato.js'
import { ir, vistaPropia } from '../navegacion.js'
import { ESPERA_CACHE_PUBLICA_MS, useAvisosDelTorneo, useLlamadaEspaciada } from '../tiempo-real.js'
import type { PestanaPublica } from '../navegacion.js'

/**
 * La portada de la web: los torneos del club, para cualquiera.
 *
 * Es lo primero que ve quien abre la web sin cuenta, y está pensada para el que
 * NO juega: el familiar que sigue el torneo desde casa, el socio que no fue, el
 * de otro club que quiere ver cómo le va a un conocido. Por eso arranca por lo
 * que se está jugando y lo cuenta como una transmisión: el torneo en vivo
 * arriba, con su avance, y debajo las mesas de la ronda con el marcador grande.
 *
 * Los torneos van en tres grupos (en juego, lo que viene, lo que pasó) y no en
 * una lista larga: en el teléfono una sola lista obliga a bajar por todo lo que
 * viene para encontrar el resultado del domingo pasado. Sin cuenta, cada grupo
 * es una pestaña del menú de abajo; con sesión, un segmento de «Torneos».
 *
 * Se entera sola de lo que pasa en el torneo destacado: escucha sus avisos por
 * el socket (ver `tiempo-real.ts`). La tira de mesas sigue las manos; esta
 * pantalla, lo que cambia la ronda entera (se armó, se cerró una mesa), que es
 * de donde salen el avance y la tabla. El botón «Actualizar» sigue estando.
 */

export type Grupo = 'vivo' | 'proximos' | 'resultados'

const ESTADO: Record<Grupo, EstadoVitrina> = {
  vivo: 'en_curso',
  proximos: 'inscripcion',
  resultados: 'finalizado',
}

/**
 * Los torneos del grupo que juega el que tiene sesión.
 *
 * Los terminados salen del historial, que es una sola consulta; los otros, de
 * sus torneos en curso y en inscripción, que son pocos. Pedir todos sus torneos
 * costaría uno por cada uno que jugó. Si la pregunta falla, la vitrina se ve
 * igual, sin las marcas.
 */
async function propiosDe(
  repo: Awaited<ReturnType<typeof obtenerRepositorio>>,
  grupo: Grupo,
): Promise<string[]> {
  try {
    if (grupo === 'resultados') return (await repo.resumenDeCarrera()).torneos.map((t) => t.id)
    return (await repo.listarMisTorneos(['en_curso', 'inscripcion'])).map((t) => t.id)
  } catch {
    return []
  }
}

interface Datos {
  torneos: TorneoPublico[]
  /** El que se cuenta en grande: el primero de los que se juegan. */
  destacado: TorneoPublico | null
  rondas: RondaPublica[]
  posiciones: PosicionPublica[]
  /** Los torneos que juega el que tiene sesión: esos se abren como suyos. */
  propios: ReadonlySet<string>
}

/**
 * Un grupo de la vitrina (En vivo, Próximos o Resultados): le pide al servidor
 * solo ese estado. Con sesión va adentro de «Torneos», que ya pone el título y
 * los segmentos, así que acá no lleva ni la portada ni el título.
 */
export function EnVivo({ sinCuenta, grupo }: { sinCuenta: boolean; grupo: Grupo }) {
  /* Sube con cada «Actualizar» y es la llave de la tira de mesas: así se vuelve
     a pedir junto con el resto en vez de quedarse con el marcador viejo. */
  const [vuelta, setVuelta] = useState(0)

  const pedir = useCallback(async (): Promise<Datos> => {
    const repo = await obtenerRepositorio()
    const [torneos, mios] = await Promise.all([
      repo.listarTorneosPublicos(ESTADO[grupo]),
      sinCuenta ? [] : propiosDe(repo, grupo),
    ])
    const propios = new Set(mios)
    const destacado = torneos.find((t) => t.estado === 'en_curso') ?? null
    if (!destacado) return { torneos, destacado, rondas: [], posiciones: [], propios }

    const [rondas, posiciones] = await Promise.all([
      repo.rondasPublicas(destacado.id),
      destacado.rondasCerradas > 0 ? repo.posicionesPublicas(destacado.id) : [],
    ])
    return { torneos, destacado, rondas, posiciones, propios }
  }, [grupo, sinCuenta])

  const { datos, error, cargando, recargar, refrescar } = useCarga(pedir)

  const pronto = useLlamadaEspaciada(refrescar, 3_000, ESPERA_CACHE_PUBLICA_MS)
  useAvisosDelTorneo(datos?.destacado?.id ?? null, true, (aviso) => {
    if (aviso.tipo !== 'mesa') pronto()
  })

  const actualizar = useCallback(() => {
    setVuelta((n) => n + 1)
    recargar()
  }, [recargar])

  if (error) return <Problema mensaje={error} alReintentar={recargar} />
  if (!datos) return cargando ? <Cargando que="Buscando los torneos…" /> : null

  const { torneos, destacado, rondas, posiciones, propios } = datos

  /* El torneo que juega se abre en su pantalla de jugador y no en la vitrina:
     ahí ve cuál es su mesa y puede cargar las manos. */
  const abrir = (torneo: TorneoPublico, pestana: PestanaPublica = 'resumen') =>
    ir(
      propios.has(torneo.id)
        ? vistaPropia(torneo.id, pestana)
        : { nombre: 'vivo-torneo', id: torneo.id, pestana },
    )

  const delGrupo = torneos.filter((t) => t.estado === ESTADO[grupo])
  const otrosEnJuego = delGrupo.filter((t) => t.id !== destacado?.id)

  return (
    <div className="pantalla vitrina">
      {sinCuenta && grupo === 'proximos' && <h1>Próximos torneos</h1>}
      {sinCuenta && grupo === 'resultados' && <h1>Resultados</h1>}

      {sinCuenta && grupo === 'vivo' && (
        <header className="vitrina-hero">
          <p className="vitrina-kicker">
            <Icono nombre="vivo" tamano={16} />
            DomiRoyal
          </p>
          <h1>Sigue cada mesa, en vivo</h1>
          <p className="tenue">
            Las mesas, los resultados y la tabla de cada torneo del club, al momento y sin cuenta.
          </p>
        </header>
      )}

      {grupo === 'vivo' && !destacado && (
        <Vacio titulo="Ahora no se está jugando ningún torneo">
          <p>Cuando arranque uno, sus mesas y su tabla aparecen acá al momento.</p>
          {/* La lista trae solo los que se juegan y no hay cómo saber si hay
              próximos: el botón va siempre y lleva a ese grupo. */}
          <button className="boton secundario" onClick={() => ir({ nombre: 'proximos' })}>
            Ver los próximos
          </button>
        </Vacio>
      )}

      {grupo === 'vivo' && destacado && (
        <div className="vitrina-vivo">
          <Destacado
            torneo={destacado}
            rondas={rondas}
            propio={propios.has(destacado.id)}
            alAbrir={(p) => abrir(destacado, p)}
            alActualizar={actualizar}
          />

          <MesasDestacadas
            key={`${destacado.id}-${vuelta}`}
            torneoId={destacado.id}
            rondas={rondas}
            alVerTodas={() => abrir(destacado, 'mesas')}
          />

          <Punteros
            posiciones={posiciones}
            titulo="Van adelante"
            alVerTabla={() => abrir(destacado, 'posiciones')}
          />

          {otrosEnJuego.length > 0 && (
            <section className="seccion">
              <h3 className="seccion-titulo">También se juegan</h3>
              <div className="lista-vitrina">
                {otrosEnJuego.map((t) => (
                  <TarjetaTorneo
                    key={t.id}
                    torneo={t}
                    propio={propios.has(t.id)}
                    alTocar={() => abrir(t)}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {grupo !== 'vivo' &&
        (delGrupo.length === 0 ? (
          <Vacio
            titulo={
              grupo === 'proximos' ? 'No hay torneos anunciados' : 'Todavía no terminó ningún torneo'
            }
          >
            <p>
              {grupo === 'proximos'
                ? 'Cuando el club abra la inscripción de uno, aparece acá.'
                : 'Los torneos terminados quedan acá con su podio y su tabla final.'}
            </p>
          </Vacio>
        ) : (
          <div className="lista-vitrina">
            {delGrupo.map((t) => (
              <TarjetaTorneo
                key={t.id}
                torneo={t}
                propio={propios.has(t.id)}
                alTocar={() => abrir(t)}
              />
            ))}
          </div>
        ))}

      {/* La invitación a entrar va al pie y no arriba: la portada es de los que
          miran, y el que juega ya sabe dónde está el botón de la barra. */}
      {sinCuenta && (
        <section className="tarjeta vitrina-cuenta">
          <div>
            <h2>¿Juegas en el club?</h2>
            <p className="tenue">
              Entra con tu usuario para ver en qué mesa te toca, tu posición y tu historial.
            </p>
          </div>
          <button className="boton principal" onClick={() => ir({ nombre: 'ingreso' })}>
            <Icono nombre="entrar" tamano={18} />
            Entrar
          </button>
        </section>
      )}
    </div>
  )
}

/**
 * El torneo en vivo, contado en grande.
 *
 * La barra no es la de rondas del torneo (esa está adentro) sino la de la ronda
 * que se juega: cuántas mesas ya terminaron. Es el número que cambia mientras
 * se mira, y el que dice si falta poco o mucho para la siguiente.
 */
function Destacado({
  torneo,
  rondas,
  propio,
  alAbrir,
  alActualizar,
}: {
  torneo: TorneoPublico
  rondas: RondaPublica[]
  /** Si lo juega el que mira: el botón principal lo lleva a su mesa. */
  propio: boolean
  alAbrir: (pestana?: PestanaPublica) => void
  alActualizar: () => void
}) {
  const ronda = rondaDelMomento(rondas)
  const jugandose = ronda !== null && ronda.estado !== 'cerrada'
  const avance = ronda && ronda.mesas > 0 ? ronda.mesasCargadas / ronda.mesas : 0

  return (
    <article className="destacado">
      <header className="destacado-cabecera">
        <span className="destacado-club acortar">{torneo.organizacion}</span>
        {propio && <span className="pastilla info">Juegas</span>}
        <PastillaVitrina estado={torneo.estado} />
      </header>

      <button className="destacado-titulo" onClick={() => alAbrir()}>
        <h2>{torneo.nombre}</h2>
        <Icono nombre="flecha" tamano={20} />
      </button>

      <p className="destacado-meta tenue">
        {fechaLarga(torneo.fecha)} · {cuantosCompiten(torneo.participantes, torneo.modalidad)}
      </p>

      <div className="destacado-ronda">
        <div className="destacado-ronda-texto">
          <span>
            {jugandose ? (
              <>
                Se juega la <b>ronda {ronda.numero}</b> de {torneo.numeroRondas}
              </>
            ) : (
              <>
                {torneo.rondasCerradas} de {torneo.numeroRondas} rondas cerradas
              </>
            )}
          </span>
          {ronda && (
            <span className="tenue">
              {ronda.mesasCargadas}/{ronda.mesas} mesas
            </span>
          )}
        </div>
        <div
          className="barra-avance"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(avance * 100)}
          aria-label="Mesas terminadas de la ronda"
        >
          <span style={{ width: `${Math.max(avance * 100, 3)}%` }} />
        </div>
      </div>

      <dl className="destacado-cifras">
        <div>
          <dt>Ronda</dt>
          <dd>
            {ronda?.numero ?? torneo.rondasCerradas}
            <small>/{torneo.numeroRondas}</small>
          </dd>
        </div>
        <div>
          <dt>Jugando</dt>
          <dd>{ronda && jugandose ? ronda.mesas - ronda.mesasCargadas : 0}</dd>
        </div>
        <div>
          <dt>Terminadas</dt>
          <dd>{ronda?.mesasCargadas ?? 0}</dd>
        </div>
      </dl>

      <div className="destacado-acciones">
        {propio ? (
          <button className="boton principal" onClick={() => alAbrir()}>
            Ver mi mesa
          </button>
        ) : (
          <button className="boton principal" onClick={() => alAbrir('mesas')}>
            Ver las mesas
          </button>
        )}
        <button className="boton secundario" onClick={alActualizar} aria-label="Actualizar">
          <Icono nombre="refrescar" tamano={16} />
          <span className="solo-ancho">Actualizar</span>
        </button>
      </div>
    </article>
  )
}
